import { useState } from "react";
import { CheckCircle, Mail, User } from "lucide-react";

import { Button } from "../shadcn/components/ui/Button";
import { Input } from "../shadcn/components/ui/Input";
import { Progress } from "../shadcn/components/ui/Progress";
import { RadioGroup, RadioGroupItem } from "../shadcn/components/ui/RadioGroup";
import { Label } from "../shadcn/components/ui/Label";
import { Navbar } from "../Component/Navbar";
import { Footer } from "../Component/Footer";

export default function DonationDetails() {
  const [selectedAmount, setSelectedAmount] = useState("99");
  const [customAmount, setCustomAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("card");

  // Calculate progress percentage
  const currentAmount = 544024;
  const goalAmount = 750000;
  const progressPercentage = (currentAmount / goalAmount) * 100;

  const presetAmounts = [
    { value: "25", label: "$25" },
    { value: "50", label: "$50" },
    { value: "75", label: "$75" },
    { value: "99", label: "$99" },
    { value: "200", label: "$200" },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-purple-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-bold text-center">
              Raise Funds For Clean & Healthy Food
            </h1>
            <p className="mt-4 text-lg text-center text-purple-100">
              Help us provide nutritious meals to those in need
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
                <div className="aspect-w-16 aspect-h-9 relative rounded-lg overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt="Campaign"
                    width={800}
                    height={450}
                    className="object-cover"
                  />
                </div>

                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      About This Campaign
                    </h2>
                    <p className="mt-4 text-gray-600">
                      We are raising funds to provide clean and healthy food to
                      underprivileged communities. Your donation will help us
                      ensure that families have access to nutritious meals and
                      proper sustenance.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Why This Matters
                    </h3>
                    <ul className="mt-4 space-y-4">
                      {[
                        "Proper nutrition is essential for child development and learning",
                        "Many families struggle to put healthy food on the table",
                        "Food insecurity affects millions of people worldwide",
                        "Your support creates lasting impact in communities",
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-purple-600 mt-1 mr-2 flex-shrink-0" />
                          <span className="text-gray-600">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <img
                      src="https://borgenproject.org/wp-content/uploads/African_American.jpg"
                      alt="Impact 1"
                      width={400}
                      height={300}
                      className="rounded-lg"
                    />
                    <img
                      src="https://images.unsplash.com/photo-1582307811683-75b18a39ab71?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                      alt="Impact 2"
                      width={400}
                      height={300}
                      className="rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Donation Box */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-6">
                <div className="space-y-6">
                  {/* Progress Bar Section */}
                  <div className="w-full">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="font-medium">
                        Raised: ${currentAmount.toLocaleString()}
                      </span>
                      <span className="text-gray-500">
                        Goal: ${goalAmount.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-purple-100 rounded-full h-2.5">
                      <div
                        className="bg-purple-600 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label>Select Amount</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {presetAmounts.map(({ value, label }) => (
                        <Button
                          key={value}
                          variant={
                            selectedAmount === value ? "default" : "outline"
                          }
                          className={
                            selectedAmount === value
                              ? "bg-purple-600 hover:bg-purple-700"
                              : ""
                          }
                          onClick={() => setSelectedAmount(value)}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                    <div className="relative">
                      <Input
                        type="number"
                        placeholder="Custom Amount"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedAmount("custom");
                        }}
                        className="pl-8"
                      />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                        $
                      </span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <Label>Payment Method</Label>
                    <RadioGroup
                      value={paymentMethod}
                      onValueChange={setPaymentMethod}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="card" id="card" />
                        <Label htmlFor="card">Credit Card</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="paypal" id="paypal" />
                        <Label htmlFor="paypal">PayPal</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-4">
                    <div className="relative">
                      <Label htmlFor="firstName">First Name</Label>
                      <User className="top-9 left-1 text-gray-400 absolute h-5" />
                      <Input
                        id="firstName"
                        placeholder="Enter your first name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Last Name</Label>
                      <div className="relative">
                        <User className="top-2.5 left-1 text-gray-400 absolute h-5" />
                        <Input
                          id="lastName"
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <div className="relative">
                        <Mail className="top-3 left-1 text-gray-400 absolute h-5" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="Enter your email"
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                    size="lg"
                  >
                    Donate Now
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    Your donation is secure and encrypted.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
